/**
 * Browser-side API client.
 *
 * The chat endpoint is a POST that streams, so `EventSource` (GET-only) is out
 * and we read the response body ourselves — which is fine, since the decoder is
 * the same one the server uses against DeepSeek.
 */

import type {
  ApiResponse,
  ChatRequest,
  ChatStreamEvent,
  Conversation,
  ConversationWithMessages,
} from "../shared/types.ts";
import type { SuggestionsPayload } from "../shared/suggestions.ts";
import type { LoginRequest, SessionState } from "../shared/auth.ts";
import type { Highlight } from "../shared/highlights.ts";
import type { Refinement } from "../shared/refine.ts";
import { readSseStream } from "../shared/sse-decode.ts";

/**
 * Thrown on a 401 so callers can tell "the session ended" apart from "the
 * request failed", and send the reader back to the login screen instead of
 * showing them an error they cannot act on.
 */
export class UnauthorizedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "UnauthorizedError";
  }
}

async function request<T>(
  input: string,
  init?: RequestInit,
): Promise<T> {
  const response = await fetch(input, {
    headers: { "Content-Type": "application/json" },
    // Sessions ride on a cookie, so it has to be sent.
    credentials: "same-origin",
    ...init,
  });

  const body = (await response.json()) as ApiResponse<T>;

  if (!body.ok) {
    if (response.status === 401) throw new UnauthorizedError(body.error);
    throw new Error(body.error);
  }
  return body.data;
}

export const api = {
  listConversations: (): Promise<readonly Conversation[]> =>
    request("/api/conversations"),

  createConversation: (): Promise<Conversation> =>
    request("/api/conversations", { method: "POST" }),

  getConversation: (id: string): Promise<ConversationWithMessages> =>
    request(`/api/conversations/${id}`),

  renameConversation: (id: string, title: string): Promise<Conversation> =>
    request(`/api/conversations/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ title }),
    }),

  suggestions: (): Promise<SuggestionsPayload> => request("/api/suggestions"),

  refine: (content: string): Promise<Refinement> =>
    request("/api/refine", {
      method: "POST",
      body: JSON.stringify({ content }),
    }),

  highlights: (conversationId: string): Promise<readonly Highlight[]> =>
    request(`/api/conversations/${conversationId}/highlights`),

  createHighlight: (input: {
    messageId: string;
    quote: string;
    note?: string;
  }): Promise<Highlight> =>
    request("/api/highlights", { method: "POST", body: JSON.stringify(input) }),

  updateHighlight: (id: string, note: string | null): Promise<Highlight> =>
    request(`/api/highlights/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ note }),
    }),

  deleteHighlight: (id: string): Promise<{ deleted: string }> =>
    request(`/api/highlights/${id}`, { method: "DELETE" }),

  session: (): Promise<SessionState> => request("/api/auth/session"),

  login: (credentials: LoginRequest): Promise<SessionState> =>
    request("/api/auth/login", {
      method: "POST",
      body: JSON.stringify(credentials),
    }),

  logout: (): Promise<SessionState> =>
    request("/api/auth/logout", { method: "POST" }),

  deleteConversation: (id: string): Promise<{ deleted: string }> =>
    request(`/api/conversations/${id}`, { method: "DELETE" }),
};

export interface StreamHandle {
  /** Resolves when the stream ends, for any reason. */
  readonly done: Promise<void>;
  readonly cancel: () => void;
}

/**
 * Start a chat turn. Events are delivered to `onEvent` as they arrive; the
 * returned handle lets the composer's stop button abort mid-answer.
 */
export function streamChat(
  body: ChatRequest,
  onEvent: (event: ChatStreamEvent) => void,
): StreamHandle {
  const controller = new AbortController();

  const done = (async () => {
    let response: Response;
    try {
      response = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify(body),
        signal: controller.signal,
      });
    } catch (cause) {
      if (!controller.signal.aborted) {
        onEvent({ type: "error", message: describeNetworkError(cause) });
      }
      return;
    }

    // Validation failures come back as ordinary JSON, before any streaming.
    if (!response.ok) {
      const message = await readErrorBody(response);
      onEvent({ type: "error", message });
      return;
    }

    if (!response.body) {
      onEvent({ type: "error", message: "The server sent an empty response." });
      return;
    }

    try {
      for await (const payload of readSseStream(response.body)) {
        const event = decode(payload);
        if (event) onEvent(event);
      }
    } catch (cause) {
      if (!controller.signal.aborted) {
        onEvent({ type: "error", message: describeNetworkError(cause) });
      }
    }
  })();

  return { done, cancel: () => controller.abort() };
}

function decode(payload: string): ChatStreamEvent | null {
  try {
    return JSON.parse(payload) as ChatStreamEvent;
  } catch {
    return null;
  }
}

async function readErrorBody(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as ApiResponse<never>;
    if (!body.ok) return body.error;
  } catch {
    // Fall through to the generic message.
  }
  return `Request failed (${response.status}).`;
}

function describeNetworkError(cause: unknown): string {
  const message = cause instanceof Error ? cause.message : String(cause);
  return `Lost the connection to the server. ${message}`;
}
